import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { InformationCircle as InformationCircleIcon } from "./icons";
import { cn } from "packages/dsaui/src/utils";

const bannerClasses = {
  error: {
    container: "bg-red-50 text-red-900",
    icon: "text-red-600",
  },
  info: {
    container: "bg-gray-100 text-gray-800",
    icon: "text-sky-500",
  },
  success: {
    container: "bg-green-50 text-green-900",
    icon: "text-green-600",
  },
  warning: {
    container: "bg-yellow-50 text-yellow-900",
    icon: "text-yellow-700",
  },
} as const;

export type BannerVariant = keyof typeof bannerClasses;

export interface BannerProps extends ComponentPropsWithoutRef<"div"> {
  bodyClassName?: string;
  children: ReactNode;
  icon?: ReactNode;
  iconClassName?: string;
  title?: string;
  titleClassName?: string;
  variant?: BannerVariant;
}

export const Banner = ({
  bodyClassName,
  children,
  className,
  icon,
  iconClassName,
  title,
  titleClassName,
  variant = "info",
  ...props
}: BannerProps) => {
  const styles = bannerClasses[variant];

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-md p-4",
        styles.container,
        className,
      )}
      role="status"
      {...props}
    >
      <div className={cn("shrink-0", styles.icon, iconClassName)}>
        {icon ?? <InformationCircleIcon className="size-5" />}
      </div>
      <div className="min-w-0 flex-1">
        {title ? (
          <p className={cn("text-label-sm m-0 font-medium", titleClassName)}>{title}</p>
        ) : null}
        <div
          className={cn(
            "text-label-sm wrap-break-word",
            title && "mt-1",
            bodyClassName,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default Banner;
